import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon, SendIcon, XIcon, MinusIcon } from 'lucide-react';
export interface WidgetConfig {
  name: string;
  description: string;
  greeting: string;
  prompts: string[];
  primaryColor: string;
  accentColor: string;
  theme: 'light' | 'dark';
  position: 'Bottom Right' | 'Bottom Left' | 'Floating Center' | 'Embedded Page' | 'Full Screen Assistant';
  size: 'Compact' | 'Standard' | 'Expanded' | 'Full Screen';
  avatar: string;
  tone: string;
}
const SIZE_MAP: Record<WidgetConfig['size'], {
  w: string;
  h: string;
}> = {
  Compact: {
    w: 'w-72',
    h: 'h-96'
  },
  Standard: {
    w: 'w-80',
    h: 'h-[460px]'
  },
  Expanded: {
    w: 'w-96',
    h: 'h-[520px]'
  },
  'Full Screen': {
    w: 'w-full max-w-md',
    h: 'h-[560px]'
  }
};
export function WidgetPreview({
  config,
  interactive = false
}: {
  config: WidgetConfig;
  interactive?: boolean;
}) {
  const dark = config.theme === 'dark';
  const size = SIZE_MAP[config.size];
  const alignRight = config.position !== 'Bottom Left';
  return <div className={`w-full h-full flex flex-col ${alignRight ? 'items-end' : 'items-start'} justify-end`} data-id="element-3275" data-mp="2pqcubvuyhjnt">
      <motion.div layout className={`${size.w} ${size.h} max-w-full rounded-2xl shadow-2xl overflow-hidden flex flex-col ${dark ? 'bg-slate-900' : 'bg-white'} border ${dark ? 'border-slate-700' : 'border-slate-200'}`} data-id="element-3276" data-mp="2bpsdh58lpxwy">
        {/* Header */}
        <div className="flex items-center justify-between p-4" style={{
        backgroundColor: config.primaryColor
      }} data-id="element-3277" data-mp="26rqx4s2k8cp6">
          <div className="flex items-center gap-2.5" data-id="element-3278" data-mp="1s0hzpwsf1n1h">
            <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-white font-bold backdrop-blur-sm" data-id="element-3279" data-mp="1x27bjengq4qm">
              {config.avatar || <SparklesIcon className="w-4 h-4" data-id="element-3280" data-mp="1o0ravymg9gzl" />}
            </div>
            <div data-id="element-3281" data-mp="1nzy1epw3wddr">
              <div className="text-white font-bold text-sm leading-tight" data-id="element-3282" data-mp="1o2k9qrqtmwx1">
                {config.name || 'AI Assistant'}
              </div>
              <div className="text-white/80 text-[11px] flex items-center gap-1" data-id="element-3283" data-mp="3kyyakibbhec0">
                <span className="w-1.5 h-1.5 rounded-full bg-green-300" data-id="element-3284" data-mp="3fzlr87jnmpe6" />{' '}
                Online now
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 text-white/80" data-id="element-3285" data-mp="3c0cv4w156ffg">
            <MinusIcon className="w-4 h-4" data-id="element-3286" data-mp="cibylynh8zs4" />
            <XIcon className="w-4 h-4" data-id="element-3287" data-mp="ch0w025rpy2l" />
          </div>
        </div>

        {/* Body */}
        <div className={`flex-1 overflow-y-auto p-4 space-y-3 ${dark ? 'bg-slate-900' : 'bg-slate-50'}`} data-id="element-3288" data-mp="7i7elynd1mx5">
          <div className="flex gap-2" data-id="element-3289" data-mp="3up9sx9vqzq2f">
            <div className="w-7 h-7 rounded-full flex items-center justify-center text-white flex-shrink-0" style={{
            backgroundColor: config.primaryColor
          }} data-id="element-3290" data-mp="3uo8482lt7528">
              <SparklesIcon className="w-3.5 h-3.5" data-id="element-3291" data-mp="2jbxniukcaze5" />
            </div>
            <div className={`max-w-[80%] px-3 py-2 rounded-2xl rounded-tl-sm text-sm ${dark ? 'bg-slate-800 text-slate-100' : 'bg-white text-slate-800 border border-slate-100'}`} data-id="element-3292" data-mp="yt3hd6uc7urk">
              {config.greeting || 'Hello 👋 How can I help you today?'}
            </div>
          </div>

          <div className="flex flex-col gap-2 pl-9" data-id="element-3293" data-mp="1cki3vnngee3d">
            {config.prompts.filter(Boolean).slice(0, 4).map(p => <button key={p} className={`text-left text-xs px-3 py-2 rounded-xl border transition-colors ${dark ? 'border-slate-700 text-slate-200 hover:bg-slate-800' : 'border-slate-200 text-slate-600 hover:bg-white'}`} style={{
            borderColor: config.accentColor + '55'
          }} data-id="element-3294" data-mp="1mgbqjzuvznyv">
                  {p}
                </button>)}
          </div>
        </div>

        {/* Input */}
        <div className={`p-3 border-t ${dark ? 'border-slate-700 bg-slate-900' : 'border-slate-100 bg-white'}`} data-id="element-3295" data-mp="31y80kz6byhum">
          <div className={`flex items-center gap-2 rounded-full px-3 py-2 ${dark ? 'bg-slate-800' : 'bg-slate-100'}`} data-id="element-3296" data-mp="2xxee8a8jdb3u">
            <input disabled={!interactive} placeholder="Type your message…" className={`flex-1 bg-transparent text-sm outline-none ${dark ? 'text-slate-100 placeholder:text-slate-500' : 'text-slate-700 placeholder:text-slate-400'}`} data-id="element-3297" data-mp="36xudt8m5ry3f" />
            <button className="w-7 h-7 rounded-full flex items-center justify-center text-white flex-shrink-0" style={{
            backgroundColor: config.primaryColor
          }} data-id="element-3298" data-mp="2syb62gx9eorn">
              <SendIcon className="w-3.5 h-3.5" data-id="element-3299" data-mp="20h8mjb2zvsxx" />
            </button>
          </div>
          <div className={`text-center text-[10px] mt-2 ${dark ? 'text-slate-600' : 'text-slate-400'}`} data-id="element-3300" data-mp="1vjyzg232vtd9">
            Powered by Talab
          </div>
        </div>
      </motion.div>

      {/* Launcher bubble */}
      <div className="mt-3 w-14 h-14 rounded-full flex items-center justify-center text-white shadow-xl" style={{
      backgroundColor: config.primaryColor
    }} data-id="element-3301" data-mp="ftfzu02bn8s7">
        <SparklesIcon className="w-6 h-6" data-id="element-3302" data-mp="62l6ycuklaxs" />
      </div>
    </div>;
}